import {
  ISidebarContract,
  ISidebarCommitmentItem,
  ISidebarChangeOrder,
  ISidebarCost,
  ISidebarInvoice,
  ISidebarInvoiceCost,
  ISidebarDirectCost,
  CommitmentsState,
} from './commitments.types';
import { COMMITMENTS_TYPE } from '@app/framework/constants/interaction-bar.constants';

const convertSidebarCommitmentToContract = (
  sidebarCommitment: any,
  isPrime: boolean,
): Partial<ISidebarContract> => ({
  id: sidebarCommitment.id,
  has_commitments: sidebarCommitment?.has_commitments,
  project_id: sidebarCommitment.project_id,
  type: sidebarCommitment.type,
  added_at: sidebarCommitment.added_at,
  title: sidebarCommitment.title,
  description: sidebarCommitment.description,
  approval_status: sidebarCommitment.approval_status,
  files: sidebarCommitment.user_drive_files,
  prime_contract_sovs: sidebarCommitment.prime_contract_sovs,
  convertedFromPrimeContract: isPrime,
  commitment_items: sidebarCommitment.commitment_items.map((commitmentItem) => {
    return {
      id: commitmentItem.id,
      has_commitments: commitmentItem.has_commitments,
      item: commitmentItem.item,
      start_date: commitmentItem.start_date,
      duration: commitmentItem.duration,
      budget_tag: commitmentItem.budget_tag ?? {
        id: null,
        name: '',
      },
      costs: commitmentItem.costs,
    } as ISidebarCommitmentItem;
  }),
  service_provider: sidebarCommitment.service_provider
    ? {
        name: sidebarCommitment.service_provider.company_name,
        service_provider_user_id: sidebarCommitment.service_provider.id,
      }
    : {
        name: sidebarCommitment.temporary_service_provider.name,
        temporary_service_provider_id: sidebarCommitment.temporary_service_provider.id,
      },
});

const convertSidebarCommitmentToChangeOrder = (
  sidebarCommitment: any,
): Partial<ISidebarChangeOrder> => ({
  id: sidebarCommitment.id,
  change_order_type: sidebarCommitment?.change_order_type,
  has_commitments: sidebarCommitment?.has_commitments,
  project_id: sidebarCommitment.project_id,
  contract_id: sidebarCommitment.contract_id,
  type: sidebarCommitment.type,
  added_at: sidebarCommitment.added_at,
  title: sidebarCommitment.title,
  description: sidebarCommitment.description,
  approval_status: sidebarCommitment.approval_status,
  files: sidebarCommitment.user_drive_files,
  commitment_items: sidebarCommitment.commitment_items.map((commitmentItem) => {
    return {
      id: commitmentItem.id,
      item: commitmentItem.item,
      start_date: commitmentItem.start_date,
      duration: commitmentItem.duration,
      has_commitments: commitmentItem.has_commitments,
      budget_tag: commitmentItem.budget_tag ?? {
        id: null,
        name: '',
      },
      costs: commitmentItem.costs.map((cost: ISidebarCost) => {
        return {
          ...cost,
          is_dropdown: !!cost.parent_id,
        };
      }),
    } as ISidebarCommitmentItem;
  }),
  service_provider: sidebarCommitment.service_provider
    ? {
        name: sidebarCommitment.service_provider.company_name,
        service_provider_user_id: sidebarCommitment.service_provider.id,
      }
    : {
        name: sidebarCommitment.temporary_service_provider.name,
        temporary_service_provider_id: sidebarCommitment.temporary_service_provider.id,
      },
});

const convertSidebarCommitmentToInvoice = (sidebarCommitment: any): Partial<ISidebarInvoice> => ({
  id: sidebarCommitment.id,
  project_id: sidebarCommitment.project_id,
  has_commitments: sidebarCommitment?.has_commitments,
  contract_id: sidebarCommitment.contract_id,
  type: sidebarCommitment.type,
  added_at: sidebarCommitment.added_at,
  title: sidebarCommitment.title ?? sidebarCommitment.invoice_number,
  description: sidebarCommitment.description,
  paid_status: sidebarCommitment.paid_status,
  paid_date: sidebarCommitment?.paid_status ? sidebarCommitment.paid_date : undefined,
  files: sidebarCommitment.user_drive_files,
  costs: sidebarCommitment.commitment_items.flatMap(
    (commitmentItem: ISidebarCommitmentItem): ISidebarInvoiceCost[] => {
      return commitmentItem.costs.flatMap((cost: ISidebarCost): ISidebarInvoiceCost => {
        return {
          ...cost,
          is_dropdown: true, // is_dropdown should always be true for invoices
          budget_tag: commitmentItem.budget_tag,
          item: { ...commitmentItem.item },
        };
      });
    },
  ),
  service_provider: sidebarCommitment.service_provider
    ? {
        name: sidebarCommitment.service_provider.company_name,
        service_provider_user_id: sidebarCommitment.service_provider.id,
      }
    : {
        name: sidebarCommitment.temporary_service_provider.name,
        temporary_service_provider_id: sidebarCommitment.temporary_service_provider.id,
      },
});

const convertSidebarCommitmentToDirectCost = (
  sidebarCommitment: any,
): Partial<ISidebarDirectCost> => ({
  id: sidebarCommitment.id,
  project_id: sidebarCommitment.project_id,
  vendor: sidebarCommitment.vendor,
  has_commitments: sidebarCommitment?.has_commitments,
  type: sidebarCommitment.type,
  added_at: sidebarCommitment.added_at,
  title: sidebarCommitment.title,
  description: sidebarCommitment.description,
  paid_date: sidebarCommitment.paid_date,
  paid_status: sidebarCommitment.paid_status,
  files: sidebarCommitment.user_drive_files,
  commitment_items: sidebarCommitment.commitment_items.map((commitmentItem) => {
    return {
      id: commitmentItem.id,
      item: commitmentItem.item,
      budget_tag: commitmentItem.budget_tag ?? {
        id: null,
        name: '',
      },
      has_commitments: commitmentItem.has_commitments,
      costs: commitmentItem.costs,
    };
  }),
});

export const loadSidebarCommitment = (
  state: CommitmentsState,
  action: { sidebarCommitment: any },
) => {
  switch (action.sidebarCommitment.type) {
    case COMMITMENTS_TYPE.PRIME_CONTRACTS:
    case COMMITMENTS_TYPE.CONTRACTS:
      return {
        ...state,
        isLoadingCommitment: false,
        sideBarChanged: false,
        sideBarContract: convertSidebarCommitmentToContract(
          action.sidebarCommitment,
          action.sidebarCommitment.type === COMMITMENTS_TYPE.PRIME_CONTRACTS,
        ) as ISidebarContract,
      };
    case COMMITMENTS_TYPE.CHANGE_ORDER:
      return {
        ...state,
        sideBarChanged: false,
        isLoadingCommitment: false,
        sideBarChangeOrder: convertSidebarCommitmentToChangeOrder(
          action.sidebarCommitment,
        ) as ISidebarChangeOrder,
      };
    case COMMITMENTS_TYPE.INVOICES:
      return {
        ...state,
        isLoadingCommitment: false,
        sideBarChanged: false,
        sideBarInvoice: convertSidebarCommitmentToInvoice(
          action.sidebarCommitment,
        ) as ISidebarInvoice,
      };
    case COMMITMENTS_TYPE.DIRECT_COST:
      return {
        ...state,
        isLoadingCommitment: false,
        sideBarChanged: false,
        sideBarDirectCost: convertSidebarCommitmentToDirectCost(
          action.sidebarCommitment,
        ) as ISidebarDirectCost,
      };
    default:
      console.error('Could not handle sidebar commitment of type', action.sidebarCommitment.type);
      return {
        ...state,
        sideBarChanged: false,
        isLoadingCommitment: false,
      };
  }
};
